import React from 'react';
import { 
  FiHome, 
  FiSettings, 
  FiUsers, 
  FiBarChart2, 
  FiMail, 
  FiCalendar,
  FiBell,
  FiSearch,
  FiMenu,
  FiClock,
  FiStar,
  FiTrendingUp
} from 'react-icons/fi';

const Dashboard = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Main Content */}
      <div className="p-8">
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <FiMenu className="w-6 h-6 text-gray-600" />
            </button>
            
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-80"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button className="relative p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <FiBell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-700">Admin</p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                JD
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Dashboard Overview</h2>
          <p className="text-gray-600">Welcome back! Here's what's happening with your platform.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total Users', value: '24.5K', change: '+12%', icon: FiUsers, color: 'bg-blue-500' },
            { label: 'Revenue', value: '$45.2K', change: '+8%', icon: FiTrendingUp, color: 'bg-green-500' },
            { label: 'Active Sessions', value: '2,345', change: '+23%', icon: FiBarChart2, color: 'bg-purple-500' },
            { label: 'Avg. Rating', value: '4.8', change: '+5%', icon: FiStar, color: 'bg-yellow-500' },
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg bg-opacity-10`}>
                  <stat.icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
                </div>
                <span className="text-sm font-semibold text-green-500 bg-green-100 px-2 py-1 rounded">
                  {stat.change}
                </span>
              </div>
              <h3 className="text-gray-600 text-sm mb-1">{stat.label}</h3>
              <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Coming Soon Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl shadow-xl mb-8">
          {/* Animated background elements */}
          <div className="absolute inset-0">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-6 md:mb-0">
                <div className="flex items-center space-x-2 mb-4">
                  <FiClock className="w-6 h-6 text-yellow-300" />
                  <span className="text-yellow-300 font-semibold tracking-wide">COMING SOON</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Advanced Analytics Dashboard
                </h3>
                <p className="text-indigo-100 text-lg max-w-2xl">
                  We're working on something amazing! Get ready for powerful insights, 
                  real-time data visualization, and advanced reporting tools.
                </p>
                
                {/* Feature list */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  {[
                    'Real-time data sync',
                    'Custom reports',
                    'AI-powered insights',
                    'Interactive charts',
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 text-indigo-100">
                      <div className="w-1.5 h-1.5 bg-yellow-300 rounded-full"></div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                {/* Subscribe form */}
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="px-6 py-3 rounded-lg bg-white bg-opacity-20 text-white placeholder-indigo-200 border border-indigo-400 focus:outline-none focus:ring-2 focus:ring-white flex-grow"
                  />
                  <button className="px-8 py-3 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 transition-colors shadow-lg whitespace-nowrap">
                    Notify Me
                  </button>
                </div>
              </div>
              
              {/* Coming Soon illustration */}
              <div className="relative">
                <div className="w-48 h-48 md:w-64 md:h-64 bg-white bg-opacity-10 rounded-2xl backdrop-blur-lg p-6 flex flex-col items-center justify-center border border-white border-opacity-20">
                  <FiBarChart2 className="w-16 h-16 text-white mb-4" />
                  <div className="w-full h-2 bg-white bg-opacity-20 rounded-full mb-3">
                    <div className="w-3/4 h-full bg-yellow-300 rounded-full"></div>
                  </div>
                  <p className="text-white font-semibold">75% Ready</p>
                  <p className="text-indigo-200 text-sm mt-2">Launching in Q2 2024</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Coming Soon Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: 'Mobile App',
              description: 'Native mobile apps for iOS and Android coming soon',
              icon: '📱',
              progress: 60,
            },
            {
              title: 'API Access',
              description: 'Public API for custom integrations',
              icon: '🔌',
              progress: 40,
            },
            {
              title: 'Team Collaboration',
              description: 'Real-time collaboration features',
              icon: '👥',
              progress: 30,
            },
          ].map((item, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">{item.icon}</div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h4>
              <p className="text-gray-600 text-sm mb-4">{item.description}</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-indigo-600 h-2 rounded-full" 
                  style={{ width: `${item.progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{item.progress}% complete</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;